"use client";

import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { Cookie, X, ChevronRight, ShieldCheck } from "lucide-react";

const STORAGE_KEY = "wolistic_cookie_consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const t = useTranslations("cookieConsent");

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Kısa gecikme — sayfa yüklendikten sonra göster
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    setVisible(false);
  };

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, "declined");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-4 md:px-6 md:pb-6 pointer-events-none"
      role="dialog"
      aria-label={t("ariaLabel")}
      aria-live="polite"
    >
      <div className="max-w-2xl mx-auto pointer-events-auto">
        <div className="bg-white rounded-[1.5rem] shadow-[0_8px_40px_rgba(0,0,0,0.15)] border border-gray-100 overflow-hidden animate-slide-up">
          {/* Top accent */}
          <div className="h-1 w-full bg-gradient-to-r from-[#A68F72] to-[#D4C5A9]" />

          <div className="p-5 md:p-6">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className="w-10 h-10 rounded-2xl bg-[#A68F72]/10 flex items-center justify-center text-[#A68F72] shrink-0 mt-0.5">
                <Cookie size={20} />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h2 className="font-bold text-[#404041] text-base">
                    {t("title")}
                  </h2>
                  <button
                    onClick={decline}
                    className="text-gray-400 hover:text-gray-600 transition-colors shrink-0 -mt-0.5"
                    aria-label={t("close")}
                  >
                    <X size={18} />
                  </button>
                </div>

                <p className="text-gray-500 text-sm font-light leading-relaxed mb-4">
                  {t("description")}{" "}
                  <Link
                    href="/veri-kullanimi-cerezler"
                    className="text-[#A68F72] font-medium hover:underline inline-flex items-center gap-0.5"
                  >
                    {t("moreInfo")} <ChevronRight size={12} />
                  </Link>
                </p>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <button
                    onClick={accept}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-[#A68F72] text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#947d61] transition-colors shadow-sm shadow-[#A68F72]/20"
                  >
                    <ShieldCheck size={15} />
                    {t("acceptAll")}
                  </button>

                  <Link
                    href="/veri-kullanimi-cerezler"
                    onClick={decline}
                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 border border-gray-200 text-gray-600 px-5 py-2.5 rounded-xl font-semibold text-sm hover:border-[#A68F72] hover:text-[#A68F72] transition-colors"
                  >
                    {t("essentialOnly")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
